'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { ALLOW_PUBLIC_REGISTRATION } from '@/lib/registrationConfig';

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const { t } = useLanguage();
  const { user, loading } = useAuth();

  const navLinks = [
    { label: t?.nav?.home ?? 'Home', href: '/' },
    { label: t?.footer?.about ?? 'About', href: '/about' },
    { label: t?.pricingPage?.label ?? 'Pricing', href: '/pricing' },
    { label: t?.footer?.contact ?? 'Contact', href: '/contact' },
    { label: t?.nav?.demo ?? 'Try Demo', href: '/demo' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/95 backdrop-blur-xl border-b border-border shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-14 2xl:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3.5 hover:opacity-85 transition-opacity shrink-0 min-w-0">
            <AppLogo size={64} className="sm:hidden" />
            <AppLogo size={72} className="hidden sm:block" />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-extrabold text-[1.25rem] tracking-tight text-foreground">
                AIMakerStudio
              </span>
              <span className="hidden sm:block text-[10px] font-600 text-primary/70 tracking-widest uppercase">
                Dream Builder Platform
              </span>
            </div>
          </Link>

          {/* Center nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link, index) => {
              const isDemoLink = link?.href === '/demo';
              return (
                <Link
                  key={`nav-${link?.href}-${index}`}
                  href={link?.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    pathname === link?.href
                      ? 'text-primary bg-primary/10'
                      : isDemoLink
                        ? 'relative text-primary font-semibold demo-nav-glow hover:bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                >
                  {link?.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />

            {user ? (
              <Link
                href="/welcome"
                className="btn-primary px-5 py-2 rounded-lg text-sm font-600"
              >
                {t?.nav?.dashboard ?? 'Dashboard'}
              </Link>
            ) : !loading ? (
              <>
                <Link
                  href="/sign-up-login-screen"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t?.nav?.login ?? 'Log In'}
                </Link>

                <Link
                  href={ALLOW_PUBLIC_REGISTRATION ? '/sign-up-login-screen' : '/sign-up-login-screen?tab=signup'}
                  className="btn-primary px-5 py-2 rounded-lg text-sm font-600"
                >
                  {t?.nav?.startDreaming ?? 'Start Dreaming'}
                </Link>
              </>
            ) : (
              <div className="w-28 h-9 rounded-lg bg-white/5 animate-pulse" />
            )}
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-card/95 backdrop-blur-xl border-b border-border fade-in">
          <div className="px-6 py-4 flex flex-col gap-2">
            {navLinks?.map((link, index) => (
              <Link
                key={`mobile-nav-${link?.href}-${index}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  pathname === link?.href
                    ? 'text-primary bg-primary/10'
                    : link?.href === '/demo' ?'relative text-primary font-semibold demo-nav-glow hover:bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-border flex flex-col gap-2">
              <div className="flex items-center px-4 py-2">
                <LanguageSelector />
              </div>
              {user ? (
                <Link
                  href="/welcome"
                  onClick={() => setMobileOpen(false)}
                  className="btn-primary px-4 py-2 rounded-lg text-sm text-center"
                >
                  {t?.nav?.dashboard ?? 'Dashboard'}
                </Link>
              ) : !loading ? (
                <>
                  <Link
                    href="/sign-up-login-screen"
                    onClick={() => setMobileOpen(false)}
                    className="btn-secondary px-4 py-2 rounded-lg text-sm text-center"
                  >
                    {t?.nav?.login ?? 'Log In'}
                  </Link>
                  <Link
                    href={ALLOW_PUBLIC_REGISTRATION ? '/sign-up-login-screen' : '/sign-up-login-screen?tab=signup'}
                    onClick={() => setMobileOpen(false)}
                    className="btn-primary px-4 py-2 rounded-lg text-sm text-center"
                  >
                    {t?.nav?.startDreaming ?? 'Start Dreaming'}
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}